/* eslint-disable no-unneeded-ternary */
import { showNotification } from '@mantine/notifications'
import { MoipCreditCard } from 'moip-sdk-js'

import { normalizeHolder } from './Fields'

// eslint-disable-next-line no-unused-vars
async function wirecardInstance({ number, cvc, expirationMonth, expirationYear }) {
  // dynamic import external lib
  const jsencrypt = typeof window !== 'undefined' ? (await import('jsencrypt')).default : null

  const decodedKey =
    typeof window !== 'undefined'
      ? window.atob(process.env.NEXT_PUBLIC_WIRECARD_B64_PUBLIC_KEY)
      : ''

  return MoipCreditCard.setEncrypter(jsencrypt, 'js')
    .setPubKey(decodedKey)
    .setCreditCard({ number, cvc, expirationMonth, expirationYear })
    .hash()
    .then(hash => ({ hash }))
}

async function pagSeguroInstance(
  { holder, number, cvc, expMonth, expYear },
  paymentGateway,
  isBackup
) {
  if (typeof window === 'undefined') return null

  const card = await window.PagSeguro?.encryptCard({
    publicKey: process.env.NEXT_PUBLIC_PAGSEGURO_B64_PUBLIC_KEY,
    holder,
    number,
    expMonth,
    expYear: `20${expYear}`,
    securityCode: cvc
  })

  if (!card) {
    throw new Error(
      'Houve um erro durante o cadastro pelo Gateway. Atualize a página e tente novamente.'
    )
  }

  if (card?.hasErrors && !isBackup) {
    const errorMessage = {
      INVALID_NUMBER: 'Número de cartão inválido',
      INVALID_SECURITY_CODE: 'Código de segurança do cartão inválido',
      INVALID_EXPIRATION_MONTH: 'Mês inválido',
      INVALID_EXPIRATION_YEAR: 'Ano inválido',
      INVALID_HOLDER: 'Nome inválido'
    }
    const message = errorMessage[card?.errors?.[0]?.code] || JSON.stringify(card?.errors || card)
    throw new Error(message)
  }

  return { hash: card.encryptedCard || null }
}

async function iuguInstance({ holder, number, cvc, expMonth, expYear }, paymentGateway, isBackup) {
  if (typeof window === 'undefined') return null

  const { clientConfig } = paymentGateway || {}

  const isTest =
    clientConfig?.env === 'test' ||
    !(!!process.env.NEXT_PUBLIC_VERCEL_ENV && process.env.NEXT_PUBLIC_VERCEL_ENV === 'production')

  const Iugu = window.Iugu

  await Iugu?.setAccountID(clientConfig?.accountId || process.env.NEXT_PUBLIC_IUGU_ACCOUNT_ID)
  await Iugu?.setTestMode(isTest)

  const nameArray = typeof holder === 'string' && !!holder.length ? holder.split(' ') : []
  const [first, ...last] = nameArray
  const firstName = first || ''
  const lastName = Array.isArray(last) && !!last?.length ? last.join(' ') : ''

  const card = await Iugu?.CreditCard(
    `${number}`,
    `${expMonth}`,
    `${expYear}`,
    firstName,
    lastName,
    `${cvc}`
  )

  return new Promise((resolve, reject) =>
    Iugu?.createPaymentToken(card, response => {
      if (response.errors && !isBackup) {
        const errorMessage = {
          number: 'Número de cartão inválido',
          record_invalid: 'Número de cartão inválido',
          verification_value: 'Código de segurança do cartão inválido',
          expiration: 'Mês inválido',
          expiration_month: 'Mês inválido',
          expiration_year: 'Ano inválido',
          first_name: 'Nome inválido',
          last_name: 'Nome inválido',
          full_name: 'Nome inválido'
        }

        const message =
          errorMessage[Object.keys(response.errors)?.[0]] ||
          'Falha na validação do cartão. Revise os dados e tente novamente.'
        if (isTest && !!response.errors?.record_invalid) {
          reject(message)
        } else throw new Error(message)
      } else resolve({ hash: response.id || null })
    })
  )
}

async function cieloInstance({ holder, number, cvc, expMonth, expYear }, paymentGateway, isBackup) {
  if (typeof window === 'undefined') return null

  const { clientConfig = {} } = paymentGateway || {}

  const Cielo = window.Cielo

  return new Promise((resolve, reject) => {
    const bpsilentFormValues = {
      'bp-sop-cardholdername': holder,
      'bp-sop-cardnumber': number,
      'bp-sop-cardcvv': cvc,
      'bp-sop-cardexpirationdate': `${expMonth}/20${expYear}`
    }

    const errorFields = {
      AccessToken: 'Token de acesso',
      'bp-sop-cardholdername': 'Nome',
      'bp-sop-cardnumber': 'Número',
      'bp-sop-cardexpirationdate': 'Data de validade',
      'bp-sop-cardcvv': 'Código de segurança',
      'bp-sop-cardtype': 'Tipo de cartão'
    }

    const options = {
      accessToken: clientConfig.accessToken || null,
      environment: clientConfig.env || 'sandbox',
      onSuccess: function (e) {
        resolve({ hash: e?.CardToken || null })
      },
      onError: function (error) {
        const { Code, Text } = error || {}
        const message = `${Code ? Code : ''}${Text ? `: ${Text}.` : ''}`
        if (!isBackup) reject(new Error(message))
        else return { hash: null }
      },
      onInvalid: function (errors) {
        const { Field, Message } = errors?.[0] || {}
        const message = `${errorFields[Field] || Field || ''}${Message ? ` ${Message}.` : ''}`
        if (!isBackup) throw new Error(message)
        else return { hash: null }
      },
      language: 'PT',
      cvvrequired: false,
      enableTokenize: true
    }
    return Cielo?.bpSop_silentOrderPost(options, bpsilentFormValues)
  })
}

async function cognaInstance(
  { holder, number, cvc, expMonth, expYear, taxDocument },
  paymentGateway,
  isBackup
) {
  if (typeof window === 'undefined') return null

  const { clientConfig = {} } = paymentGateway || {}
  const { publicKey, keyId } = clientConfig || {}

  if (!publicKey && !isBackup) {
    throw new Error('Chave de criptografia não encontrada para o gateway Cogna.')
  }
  if (!publicKey && isBackup) return null

  try {
    const JSEncrypt = (await import('jsencrypt')).default
    const rsa = new JSEncrypt()

    rsa.setPublicKey(publicKey)

    const cleanTaxDocument = String(taxDocument || '').replace(/\D/g, '')
    const formattedMonth = String(expMonth || '').padStart(2, '0')

    const encryptedNumber = rsa.encrypt(String(number || ''))
    const encryptedMonth = rsa.encrypt(formattedMonth)
    const encryptedYear = rsa.encrypt(String(expYear || ''))
    const encryptedCvc = rsa.encrypt(String(cvc || ''))
    const encryptedHolder = rsa.encrypt(String(holder || ''))
    const encryptedCpfHolder = rsa.encrypt(cleanTaxDocument)

    if (
      !encryptedNumber ||
      !encryptedMonth ||
      !encryptedYear ||
      !encryptedCvc ||
      !encryptedHolder ||
      !encryptedCpfHolder
    ) {
      throw new Error('Falha ao criptografar os dados do cartão.')
    }

    const prefix = keyId ? `${keyId}|` : ''

    return {
      cpfHolder: `${prefix}${encryptedCpfHolder}`,
      number: `${prefix}${encryptedNumber}`,
      expMonth: `${prefix}${encryptedMonth}`,
      expYear: `${prefix}${encryptedYear}`,
      securityCode: `${prefix}${encryptedCvc}`,
      holder: `${prefix}${encryptedHolder}`
    }
  } catch (error) {
    if (!isBackup) {
      const message =
        typeof error === 'string'
          ? error
          : error?.message ||
          'Houve um erro durante a criptografia do cartão. Verifique os dados e tente novamente.'

      throw new Error(message)
    }
    return null
  }
}

async function celcoinInstance(
  { holder, number, cvc, expMonth, expYear },
  paymentGateway,
  isBackup
) {
  if (typeof window === 'undefined') return null

  const { clientConfig } = paymentGateway || {}

  const isProduction = clientConfig?.env
    ? clientConfig.env === 'production'
    : process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'

  try {
    const BaasCard = window.BaasCard
    if (!BaasCard) {
      throw new Error(
        'Houve um erro durante o cadastro pelo Gateway. Atualize a página e tente novamente.'
      )
    }

    const baasCard = new BaasCard(isProduction)
    await baasCard._setPublicKey()

    const card = baasCard.newCard({
      number: String(number || ''),
      holder: String(holder || '').toUpperCase(),
      expiresAt: `20${expYear}-${String(expMonth || '').padStart(2, '0')}`,
      cvv: String(cvc || '')
    })

    try {
      card.validate()
    } catch {
      throw new Error('Falha na validação do cartão. Revise os dados e tente novamente.')
    }

    const hash = card.encrypt(baasCard._publicKey)
    if (!hash) throw new Error('Falha ao criptografar os dados do cartão.')

    return { hash }
  } catch (error) {
    if (!isBackup) {
      throw new Error(
        error?.message ||
        'Houve um erro durante a criptografia do cartão. Verifique os dados e tente novamente.'
      )
    }
    return null
  }
}

const matchesGatewayCode = (gateway, code) =>
  typeof gateway?.code === 'string' && gateway.code.trim().toLowerCase().startsWith(code)

export async function cryptoToolkit(payload, option) {
  const { number, cvc, expYear, expMonth, taxDocument } = payload
  const holder = normalizeHolder(payload?.holder)

  const { paymentGateway, backupPaymentGateways = [] } = option || {}

  const pagSeguroBackup = backupPaymentGateways?.find(gateway =>
    matchesGatewayCode(gateway, 'pagseguro')
  )
  const iuguBackup = backupPaymentGateways?.find(gateway => matchesGatewayCode(gateway, 'iugu'))
  const cieloBackup = backupPaymentGateways?.find(gateway => matchesGatewayCode(gateway, 'cielo'))
  const cognaBackup = backupPaymentGateways?.find(gateway =>
    matchesGatewayCode(gateway, 'cogna_pay')
  )
  const celcoinBackup = backupPaymentGateways?.find(gateway =>
    matchesGatewayCode(gateway, 'celcoin')
  )

  const hasPagSeguro = matchesGatewayCode(paymentGateway, 'pagseguro') || !!pagSeguroBackup
  const hasIugu = matchesGatewayCode(paymentGateway, 'iugu') || !!iuguBackup
  const hasCielo = matchesGatewayCode(paymentGateway, 'cielo') || !!cieloBackup
  const hasCogna = matchesGatewayCode(paymentGateway, 'cogna_pay') || !!cognaBackup
  const hasCelcoin = matchesGatewayCode(paymentGateway, 'celcoin') || !!celcoinBackup
  const hasDummy = process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production' && paymentGateway?.code === 'dummy'

  const pagseguro = hasPagSeguro
    ? await pagSeguroInstance(
      { holder, cvc, number, expMonth, expYear },
      pagSeguroBackup || paymentGateway,
      !!pagSeguroBackup
    )
    : null

  const iugu = hasIugu
    ? await iuguInstance(
      { holder, cvc, number, expMonth, expYear },
      iuguBackup || paymentGateway,
      !!iuguBackup
    )
    : null

  const cielo = hasCielo
    ? await cieloInstance(
      { holder, cvc, number, expMonth, expYear },
      cieloBackup || paymentGateway,
      !!cieloBackup
    )
    : null

  const cognaPay = hasCogna
    ? await cognaInstance(
      { holder, cvc, number, expMonth, expYear, taxDocument },
      cognaBackup || paymentGateway,
      !!cognaBackup
    )
    : null

  const celcoin = hasCelcoin
    ? await celcoinInstance(
      { holder, cvc, number, expMonth, expYear },
      celcoinBackup || paymentGateway,
      !!celcoinBackup
    )
    : null

  const dummy = hasDummy ? { hash: '1234' } : null

  return { pagseguro, iugu, cielo, cognaPay, cogna_pay: cognaPay, celcoin, creditCard: payload, dummy }
}

export const errorHandler = error => {
  const { message, response } = error
  const errorData = response?.data || {}
  const errorKey = Object.keys(errorData)[0]

  const renderError = Array.isArray(errorData[errorKey])
    ? errorData[errorKey]?.[0]
    : errorData?.detail ||
    errorData[errorKey]?.details ||
    errorData[errorKey]?.nonFieldErrors?.[0] ||
    errorData[errorKey]?.[Object.keys(errorData[errorKey])[0]]

  return showNotification?.({
    title: 'Erro',
    message:
      renderError ||
      message ||
      'Houve um erro durante o cadastro deste cartão. Verifique os dados e tente novamente',
    color: 'red'
  })
}
